// dependencies
import "ag-grid-enterprise";
import React, { useRef, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import { useSelector } from "react-redux";
import Select from "react-select";
import { useWindowSize } from "@uidotdev/usehooks";
import { useMutation, useQueryClient } from "@tanstack/react-query";

// components
import Grid from "../../../../../Components/Grid";
import Spacer from "../../../../../Components/Spacer";
import { getCurrentEnvironment } from "../../../../../Selectors/app";
import { useCompanyFeatureFlags } from "../../../../../Api/hooks";
import { updateCompanyFeatureFlag } from "../../../../../Api/services/company";
import { getColumns } from "./internal/columns";

// styles
import css from "./CompanyFeatureFlags.styl";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";

const DEFAULT_COLUMN_DEF = {
	sortable: true,
	filter: true,
	floatingFilter: true,
	flex: 1,
	// filter: "agTextColumnFilter",
};

const categoryOptions = [
	{ value: "permanent", label: "Permanent", categories: ["permanent", "config"] },
	{ value: "ephemeral", label: "Ephemeral", categories: ["ephemeral"] },
	{ value: "all", label: "All", categories: ["permanent", "ephemeral", "config"] },
];

const CompanyFeatureFlags = (props) => {
	const { companyID } = props;

	const gridRef = useRef(null);
	const size = useWindowSize();
	const queryClient = useQueryClient();
	const [selectedCategory, setSelectedCategory] = useState(categoryOptions[0]);

	const env = useSelector(getCurrentEnvironment);
	const { data, isLoading, refetch } = useCompanyFeatureFlags(env.name, companyID);

	/**
	 * Convert boolean values to "Visible" or "Hidden". This was a request
	 * by the CSMs.
	 */
	const featureFlags = (data?.featureFlags ?? []).map((flag) => {
		const flagType = flag.type;
		let flagValue = flag.value;

		if (flagType === "boolean") {
			flagValue = flagValue ? "Visible" : "Hidden";
		}

		return {
			...flag,
			value: flagValue,
		};
	});

	/**
	 * Filter the flags based on the selected category.
	 */
	const filteredFlags = featureFlags.filter((flag) => selectedCategory.categories.includes(flag.category));

	/**
	 * This mutation will update the feature flag of a company.
	 */
	const mutation = useMutation({
		mutationFn: updateCompanyFeatureFlag,
		onSuccess: () => {
			// Invalidate and refetch
			queryClient.invalidateQueries({ queryKey: ["company-feature-flags", env, companyID] });
			refetch();
		},
	});

	if (isLoading) {
		return <div>Loading...</div>;
	}

	/**
	 * When a cell is edited, update the value in the matrix. This has to do
	 * some extra work because CSMs have requested that boolean flags use
	 * "Visible" and "Hidden" instead of "true" and "false".
	 */
	const onCellEditRequest = async (event) => {
		const slug = event.data._id;
		const flagType = event.data.type;
		let newValue = event.newValue;

		if (flagType === "boolean") {
			newValue = newValue.toLowerCase() === "visible";
		}

		await mutation.mutateAsync({
			environment: env.name,
			companyId: companyID,
			slug: slug,
			data: {
				value: newValue.toString(),
			},
		});
	};

	const columns = getColumns({ companyId: companyID });

	return (
		<div className={css.gridContainer}>
			<Grid columns={["250px", "1fr"]}>
				<Select
					defaultValue={selectedCategory}
					name="category"
					options={categoryOptions}
					isSearchable={false}
					onChange={(option) => setSelectedCategory(option)}
				/>

				<div />
			</Grid>

			<Spacer size={2} />

			<div className="ag-theme-quartz" style={{ height: size.height - 220 }}>
				<AgGridReact
					ref={gridRef}
					animateRows={false}
					defaultColDef={DEFAULT_COLUMN_DEF}
					rowData={filteredFlags}
					columnDefs={columns}
					rowSelection="multiple"
					onCellValueChanged={onCellEditRequest}
					overlayLoadingTemplate={
						'<div aria-live="polite" aria-atomic="true" style="height:100px; width:100px; background: url(https://ag-grid.com/images/ag-grid-loading-spinner.svg) center / contain no-repeat; margin: 0 auto;" aria-label="loading"></div>'
					}
				/>
			</div>
		</div>
	);
};

export default CompanyFeatureFlags;
